import * as React from 'react';
import FindControlViewState from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import getFloatingPickerProps from '../utils/getFloatingPickerProps';
import tryForceResolve from '../utils/tryForceResolve';
import updateQueryString from '../actions/updateQueryString';
import { Autofill } from 'office-ui-fabric-react/lib';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import { FloatingPeoplePicker } from 'office-ui-fabric-react/lib/FloatingPicker';
import { ITheme } from '@uifabric/styling';
import { observer } from 'mobx-react';
import { isBrowserIE } from 'owa-user-agent';
import * as ReactDOM from 'react-dom';

let styles = require('./FindPeopleControl.scss');
const classNames = require('classnames/bind').bind(styles);

export interface FindPeopleControlProps {
    viewState: FindControlViewState;
    onItemSelected: (item: FindRecipientPersonaType) => void;
    focusAutomatically: boolean;
    openPickerAutomatically: boolean;
    className?: string;
    inputAriaLabel?: string;
    onInputChanged?: (value: string) => void;
    onPaste?: (value: string) => void;
    onBlur?: () => void;
    onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    theme?: ITheme;
    onPickerHidden?: () => void;
    editingRecipientInputClassName?: string;
}

@observer
export default class FindPeopleControl extends React.Component<FindPeopleControlProps, {}> {
    private findControl: FloatingPeoplePicker;
    private input: Autofill;
    private inputElement: HTMLElement;

    constructor(props: FindPeopleControlProps) {
        super(props);
    }

    public focus = () => {
        this.input.focus();
    };

    public clearInput = () => {
        this.input.clear();
    };

    public showPicker = () => {
        if (this.findControl) {
            this.findControl.showPicker(true /*updateValue*/);
        }
    };

    public hidePicker = () => {
        if (this.findControl) {
            this.findControl.hidePicker();
        }
    };

    public onQueryStringChanged = (value: string) => {
        if (this.findControl) {
            this.findControl.onQueryStringChanged(value);
        }
    };

    public getFloatingPicker = () => {
        return this.findControl;
    };

    public getInputValue = () => {
        return this.input.value;
    };

    render() {
        let props = getFloatingPickerProps(
            this.getViewState,
            this.getFloatingPicker,
            true /*controlledComponent*/,
            this.props.theme,
            this.props.onPickerHidden
        );
        return (
            <div className={this.props.className}>
                <Autofill
                    className={classNames(styles.input, this.props.editingRecipientInputClassName)}
                    aria-label={this.props.inputAriaLabel}
                    ref={this.getInputRef}
                    onClick={this.onClick}
                    onInputValueChange={this.onInputChanged}
                    onPaste={this.onPaste}
                    onBlur={this.onBlurIfPossible()}
                    defaultVisibleValue={this.getViewState().queryString}
                    onKeyDown={this.onInputKeyDown}
                />
                <FloatingPeoplePicker
                    {...props}
                    componentRef={(ref: FloatingPeoplePicker) => (this.findControl = ref)}
                    inputElement={this.input ? this.input.inputElement : null}
                    onChange={this.onItemSelected}
                    suggestionItems={this.props.viewState.findResultSet.slice()}
                />
            </div>
        );
    }

    private onItemSelected = (item: FindRecipientPersonaType) => {
        this.props.onItemSelected(item);
        this.input.clear();
        this.hidePicker();
    };

    private getViewState = (): FindControlViewState => {
        return this.props.viewState;
    };

    private onInputChanged = (value: string) => {
        updateQueryString(this.props.viewState, value);
        if (this.props.onInputChanged) {
            this.props.onInputChanged(value);
        }

        this.onQueryStringChanged(this.input.value);
    };

    private onPaste = (ev: React.ClipboardEvent<Autofill | HTMLInputElement>) => {
        if (this.props.onPaste) {
            const inputText = ev.clipboardData.getData('Text');
            ev.preventDefault();
            this.props.onPaste(inputText);
        }
    };

    private onBlurIfPossible = () => {
        if (isBrowserIE()) {
            // If the browser is IE, then onBlur does not work correctly, so don't attach the handler for onBlur
            return null;
        }

        return (ev: React.FocusEvent<Autofill | HTMLInputElement>) => {
            if (!isBrowserIE()) {
                this.onBlur(ev.relatedTarget);
            }
        };
    };

    /**
     * focusOut handler that is attached in case we are using IE since onBlur does not return relatedTarget for IE
     */
    private focusOut = (ev: FocusEvent) => {
        this.onBlur(ev.relatedTarget as HTMLElement);
    };

    /**
     * onBlur handler
     */
    private onBlur = (target: EventTarget) => {
        let relatedTarget = target as HTMLElement;
        // If an onBlur handler is given
        // And the relatedTarget is null OR the target is not a suggestion item
        // Call the onBlur handler
        if (
            this.props.onBlur &&
            (!relatedTarget ||
                (relatedTarget.className.indexOf('ms-Suggestions-itemButton') === -1 &&
                    relatedTarget.className.indexOf('ms-Suggestions-sectionButton') === -1))
        ) {
            this.props.onBlur();
        }
    };

    private onClick = (ev: React.MouseEvent<Autofill | HTMLInputElement>) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.showPicker();
    };

    private onInputKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.props.onInputKeyDown) {
            this.props.onInputKeyDown(ev);
        }
    };

    private getInputRef = (ref: any) => {
        if (!this.input) {
            // Need to rerender for the floating picker to pick up the input element ref
            this.input = ref;
            this.input.inputElement.value = this.getViewState().queryString;
            this.forceUpdate(() => {
                if (this.props.openPickerAutomatically) {
                    if (this.props.viewState.inForceResolve) {
                        tryForceResolve(this.getViewState, this.findControl);
                    } else {
                        this.showPicker();
                    }
                }

                if (this.props.focusAutomatically) {
                    this.input.focus();
                }
            });
        }

        if (isBrowserIE()) {
            // If browser is IE, then attach the focusout event handler instead of onBlur
            // Currently, React doesn't support focusin/focusout events, so attaching/detaching the listeners manually
            if (this.inputElement) {
                this.inputElement.removeEventListener('focusout', this.focusOut);
            }

            this.inputElement = ReactDOM.findDOMNode(ref) as HTMLElement;

            if (this.inputElement) {
                this.inputElement.addEventListener('focusout', this.focusOut);
            }
        }
    };
}
